import * as THREE from 'three';
import { TelemetryRecorder, TelemetryFrame, RecordedAgentPose } from './TelemetryRecorder';
import { ReplayCameras, ReplayCameraMode } from './ReplayCameras';
import { CarModel } from '../vehicle/CarModel';
import type { TrafficManager } from '../traffic/TrafficManager';

export class ReplayEngine {
  public isPlaying: boolean = false;
  public playbackSpeed: number = 1.0; // 0.5, 1.0, 2.0
  public currentTime: number = 0;
  public replayCameras: ReplayCameras;

  constructor(camera: THREE.PerspectiveCamera, public recorder: TelemetryRecorder) {
    this.replayCameras = new ReplayCameras(camera);
  }

  public startReplay() {
    if (this.recorder.frames.length === 0) return;
    this.currentTime = this.recorder.frames[0].time;
    this.isPlaying = true;
  }

  public pause() {
    this.isPlaying = false;
  }

  public resume() {
    this.isPlaying = true;
  }

  public setSpeed(speed: number) {
    this.playbackSpeed = speed;
  }

  public setCameraMode(mode: ReplayCameraMode) {
    this.replayCameras.setMode(mode);
  }

  public seek(progress: number) {
    if (this.recorder.frames.length < 2) return;
    const startTime = this.recorder.frames[0].time;
    const endTime = this.recorder.frames[this.recorder.frames.length - 1].time;
    this.currentTime = startTime + (endTime - startTime) * Math.max(0, Math.min(1, progress));
  }

  private applyInterpolatedPose(
    entity: { position: THREE.Vector3; mesh: THREE.Object3D },
    poseA: RecordedAgentPose,
    poseB: RecordedAgentPose | undefined,
    alpha: number
  ) {
    const b = poseB || poseA;
    const x = poseA.x + (b.x - poseA.x) * alpha;
    const y = poseA.y + (b.y - poseA.y) * alpha;
    const z = poseA.z + (b.z - poseA.z) * alpha;
    const angleDiff = ((b.rotY - poseA.rotY + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
    const rotY = poseA.rotY + angleDiff * alpha;
    entity.position.set(x, y, z);
    entity.mesh.position.set(x, y, z);
    entity.mesh.rotation.y = rotY;
  }

  public update(deltaTime: number, carModel: CarModel, trafficManager?: TrafficManager): TelemetryFrame | null {
    const frames = this.recorder.frames;
    if (frames.length === 0) return null;

    if (this.isPlaying) {
      this.currentTime += deltaTime * this.playbackSpeed;
      const maxTime = frames[frames.length - 1].time;
      if (this.currentTime >= maxTime) {
        this.currentTime = maxTime;
        this.isPlaying = false;
      }
    }

    // Find surrounding frames for interpolation
    let idx = 0;
    while (idx < frames.length - 1 && frames[idx + 1].time <= this.currentTime) {
      idx++;
    }

    const frameA = frames[idx];
    const frameB = frames[Math.min(frames.length - 1, idx + 1)];

    let alpha = 0;
    const dt = frameB.time - frameA.time;
    if (dt > 0.0001) {
      alpha = Math.max(0, Math.min(1, (this.currentTime - frameA.time) / dt));
    }

    // Interpolate position and quaternion
    const posA = new THREE.Vector3(...frameA.position);
    const posB = new THREE.Vector3(...frameB.position);
    const interpPos = posA.clone().lerp(posB, alpha);

    const quatA = new THREE.Quaternion(...frameA.quaternion);
    const quatB = new THREE.Quaternion(...frameB.quaternion);
    const interpQuat = quatA.clone().slerp(quatB, alpha);

    // Apply to Car Model
    carModel.mesh.position.copy(interpPos);
    carModel.mesh.quaternion.copy(interpQuat);

    // Reconstruct surrounding AI Traffic, Bus, Cyclists & Pedestrians
    if (trafficManager) {
      if (frameA.aiVehicles) {
        for (let i = 0; i < Math.min(trafficManager.vehicles.length, frameA.aiVehicles.length); i++) {
          this.applyInterpolatedPose(
            trafficManager.vehicles[i],
            frameA.aiVehicles[i],
            frameB.aiVehicles?.[i],
            alpha
          );
        }
      }
      if (trafficManager.bus && frameA.busPose) {
        this.applyInterpolatedPose(trafficManager.bus, frameA.busPose, frameB.busPose || undefined, alpha);
      }
      if (frameA.cyclists) {
        for (let i = 0; i < Math.min(trafficManager.cyclists.length, frameA.cyclists.length); i++) {
          this.applyInterpolatedPose(
            trafficManager.cyclists[i],
            frameA.cyclists[i],
            frameB.cyclists?.[i],
            alpha
          );
        }
      }
      if (frameA.pedestrians) {
        for (let i = 0; i < Math.min(trafficManager.pedestrians.length, frameA.pedestrians.length); i++) {
          this.applyInterpolatedPose(
            trafficManager.pedestrians[i],
            frameA.pedestrians[i],
            frameB.pedestrians?.[i],
            alpha
          );
        }
      }
    }

    // Update Cockpit Instruments during replay
    carModel.cockpit.update(
      frameA.steeringAngle,
      frameA.speedKmh,
      frameA.rpm,
      frameA.blinker,
      frameA.handbrake,
      false
    );

    // Update Replay Camera
    this.replayCameras.update(interpPos, interpQuat, frameA.steeringAngle, frameA.shoulderCheck);

    return frameA;
  }

  public getProgress(): number {
    const frames = this.recorder.frames;
    if (frames.length < 2) return 0;
    const startTime = frames[0].time;
    const endTime = frames[frames.length - 1].time;
    return (this.currentTime - startTime) / (endTime - startTime);
  }
}
